import { z } from 'zod'
import {
  DUEL_ERROR_CODES,
  DUEL_ERROR_TEXT,
  type DuelSnapshot,
  duelClientMessageSchema,
  duelRoomBodySchema,
  TURN_SECONDS_DEFAULT,
  turnSecondsSchema,
} from '../duel/protocol'
import type { CommonServerMessage, ErrorCode } from '../protocol'
import type { GameState } from './engine'

export const cellSchema = z.number().int().min(0).max(8)

// ---- room settings (chosen by the creator, fixed for the room's lifetime) ----

export const roomSettingsSchema = z.object({
  /** How long each player has per move before forfeiting. */
  turnSeconds: turnSecondsSchema,
  /** Fade the mark that will vanish on the mover's next move. Off = players must remember. */
  showVanishHint: z.boolean(),
})
export type RoomSettings = z.infer<typeof roomSettingsSchema>
/** Body of POST /api/rooms: anything omitted takes the server default. */
export const roomSettingsPatchSchema = roomSettingsSchema.partial()
export type RoomSettingsPatch = z.infer<typeof roomSettingsPatchSchema>

export const DEFAULT_ROOM_SETTINGS: RoomSettings = {
  turnSeconds: TURN_SECONDS_DEFAULT,
  showVanishHint: true,
}

/**
 * XOX-9000 always plays X and opens unless the room was created with `humanFirst` (then O opens
 * every game, under the edge house rule when it is on).
 */
export const createRoomBodySchema = duelRoomBodySchema(roomSettingsPatchSchema)
export type CreateRoomBody = z.infer<typeof createRoomBodySchema>

export interface RoomSnapshot extends DuelSnapshot<GameState, RoomSettings> {
  /** Computer rooms: the human's opening mark may not go on an edge cell. */
  edgeRule: boolean
}

// ---- client -> server ----

export const clientMessageSchema = duelClientMessageSchema({ cell: cellSchema })
export type ClientMessage = z.infer<typeof clientMessageSchema>

// ---- server -> client ----

export const XOX_ERROR_CODES = [
  ...DUEL_ERROR_CODES,
  'CELL_TAKEN',
  'INVALID_CELL',
  'EDGE_OPENING',
] as const
export type XoxErrorCode = ErrorCode | (typeof XOX_ERROR_CODES)[number]

export const XOX_ERROR_TEXT: Record<(typeof XOX_ERROR_CODES)[number], string> = {
  ...DUEL_ERROR_TEXT,
  CELL_TAKEN: 'That cell is taken',
  INVALID_CELL: 'Invalid cell',
  EDGE_OPENING: "House rule: when you start, your first mark can't go on an edge",
}

export type ServerMessage =
  | CommonServerMessage<XoxErrorCode>
  | { type: 'state'; snapshot: RoomSnapshot }
